import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

interface SensorRef {
  id: string;
  name: string;
}

interface Sorting {
  dir: string;
  parameter: string;
  crop: string;
}

interface SortingDevices {
  paramDevices: string;
}

interface SortingPhoto {
  dir: string;
}

interface SensorsState {
  photosType: string;
  photosTime: string;
  sorting: Sorting;
  sortingDevices: SortingDevices;
  sortingPhoto: SortingPhoto;
  selectedField: string;
  selectedSeason: string;
  selectedSensor: string;
  selectedSensorPeriod: string[];
  selectedSecondarySensor: string;
  nextSensor: SensorRef;
  prevSensor: SensorRef;
  selectedDate: string;
  boundaryId: string;
  hasScrolled: boolean;
  activeReportChart: string;
  activeReportChartVegetation: string;
  addingSeason: boolean;
}

const initialState: SensorsState = {
  photosType: 'day',
  photosTime: '',
  sorting: {
    dir: '',
    parameter: 'name',
    crop: 'all',
  },
  sortingDevices: {
    paramDevices: 'name',
  },
  sortingPhoto: {
    dir: 'des',
  },
  selectedField: '',
  selectedSeason: '',
  selectedSensor: '',
  selectedSensorPeriod: [],
  selectedSecondarySensor: '',
  nextSensor: { id: '', name: '' },
  prevSensor: { id: '', name: '' },
  selectedDate: '',
  boundaryId: '',
  hasScrolled: false,
  activeReportChart: 'temperature',
  activeReportChartVegetation: 'leafNdvi',
  addingSeason: false,
};

export const sensorsSlice = createSlice({
  name: 'sensors',
  initialState,
  reducers: {
    selectMainSensor: (state, { payload: { id } }: PayloadAction<{ id: string }>) => {
      state.selectedSensor = id;
      return state;
    },
    selectMainSensorPeriod: (state, { payload: { period } }: PayloadAction<{ period: string[] }>) => {
      // to show dates for adding season (from dashboard/timeline)
      state.selectedSensorPeriod = period;
      return state;
    },
    resetMainSensor: (state) => {
      state.selectedSensor = '';
      return state;
    },
    selectSecondarySensor: (state, { payload: { id } }: PayloadAction<{ id: string }>) => {
      state.selectedSecondarySensor = id;
      return state;
    },
    resetSecondarySensor: (state) => {
      state.selectedSecondarySensor = '';
      return state;
    },
    selectNextSensor: (state, { payload }: PayloadAction<SensorRef>) => {
      state.nextSensor = payload;
      return state;
    },
    selectPrevSensor: (state, { payload }: PayloadAction<SensorRef>) => {
      state.prevSensor = payload;
      return state;
    },
    updateSorting: (state, { payload }: PayloadAction<Sorting>) => {
      state.sorting = payload;
      return state;
    },
    updateDevicesSorting: (state, { payload }: PayloadAction<SortingDevices>) => {
      state.sortingDevices = payload;
      return state;
    },
    updatePhotoSorting: (state, { payload }: PayloadAction<SortingPhoto>) => {
      state.sortingPhoto = payload;
      return state;
    },
    selectDate: (state, { payload }: PayloadAction<string>) => {
      state.selectedDate = payload;
      return state;
    },
    selectPhotosType: (state, { payload }: PayloadAction<string>) => {
      state.photosType = payload;
      return state;
    },
    selectPhotosTime: (state, { payload }: PayloadAction<string>) => {
      state.photosTime = payload;
      return state;
    },
    selectBoundary: (state, { payload }: PayloadAction<string>) => {
      state.boundaryId = payload;
      return state;
    },
    selectSeason: (state, { payload }: PayloadAction<string>) => {
      state.selectedSeason = payload;
      return state;
    },
    selectField: (state, { payload }: PayloadAction<string>) => {
      state.selectedField = payload;
      return state;
    },
    selectScrolledTimeline: (state, { payload }: PayloadAction<boolean>) => {
      state.hasScrolled = payload;
      return state;
    },
    selectActiveReportChart: (state, { payload }: PayloadAction<string>) => {
      state.activeReportChart = payload;
      return state;
    },
    selectActiveReportChartVegetation: (state, { payload }: PayloadAction<string>) => {
      state.activeReportChartVegetation = payload;
      return state;
    },
    selectAddingSeason: (state, { payload }: PayloadAction<boolean>) => {
      state.addingSeason = payload;
      return state;
    },
  },
});

export const {
  selectMainSensor,
  resetMainSensor,
  selectSecondarySensor,
  selectNextSensor,
  selectPrevSensor,
  resetSecondarySensor,
  updateSorting,
  updateDevicesSorting,
  updatePhotoSorting,
  selectDate,
  selectPhotosType,
  selectPhotosTime,
  selectBoundary,
  selectSeason,
  selectField,
  selectScrolledTimeline,
  selectActiveReportChart,
  selectActiveReportChartVegetation,
  selectAddingSeason,
  selectMainSensorPeriod,
} = sensorsSlice.actions;
